import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Maximize2 } from "lucide-react";
import { FadeUp, MaskedLines } from "../components/Reveal";
import Lightbox from "../components/Lightbox";

const IMG = "/images/orica";

const slides = [
  { src: `${IMG}/slide-01.jpg`, caption: "Cover / title slide" },
  { src: `${IMG}/slide-02.jpg`, caption: "Talent Snapshot" },
  { src: `${IMG}/slide-03.jpg`, caption: "Progression" },
  { src: `${IMG}/slide-04.jpg`, caption: "Actions" },
];

const snapshotDetails = [
  { src: `${IMG}/crop-population.jpg`, caption: "Population pictograms: headcount made countable at a glance" },
  { src: `${IMG}/crop-pie.jpg`, caption: "Pie-style performance graphic: one figure, one shape" },
  { src: `${IMG}/crop-dots.jpg`, caption: "Dot matrix: risk of loss as a proportion of the whole" },
  { src: `${IMG}/crop-progress.jpg`, caption: "Percentage indicators with purpose-built icons" },
  { src: `${IMG}/crop-growth.jpg`, caption: "Growth-potential scale: three tiers, one visual rhythm" },
  { src: `${IMG}/crop-priority.jpg`, caption: "High-priority talent callout: boxed, iconed, unmissable" },
];

const dataModules = [
  { src: `${IMG}/crop-risk-header.jpg`, caption: "Risk / impact matrix with status indicators", wide: true },
  { src: `${IMG}/crop-module.jpg`, caption: "Reusable information module" },
  { src: `${IMG}/crop-succession.jpg`, caption: "Succession statistics & progress scales" },
  { src: `${IMG}/crop-actions-stats.jpg`, caption: "Development-action indicators" },
  { src: `${IMG}/crop-pie.jpg`, caption: "Performance graphic" },
];

const clarityDevices = [
  "Strong typographic hierarchy",
  "Clearly divided information zones",
  "Consistent grid structures",
  "Charts and data visualisation",
  "Purpose-built icons and pictograms",
  "Percentage and population indicators",
  "Matrices for more complex information",
  "Consistent headers, footers and navigation",
  "Strategic use of scale, weight and spacing",
];

const systemTraits = [
  ["Consistent typography", "One type system across headline, label, statistic and commentary."],
  ["Repeatable grid structures", "The same column logic governs a snapshot, a matrix and a list."],
  ["Shared iconography", "Gender, diversity and mobility icons recur wherever the data does."],
  ["Visual categorisation", "Potential and performance bands read as bands, not as paragraphs."],
  ["Data matrices", "Nine-cell structures hold nested populations without losing the row."],
  ["Risk / status indicators", "Colour-coded wedges signal urgency before a word is read."],
  ["Reusable information modules", "With and without development actions: one module, repeated 18 times."],
  ["Consistent colour and hierarchy", "Brand blue carries structure; accent colour is reserved for meaning."],
];

const skills = [
  "Presentation Design",
  "Information Design",
  "Corporate Communications",
  "Data Visualisation",
  "Visual Hierarchy",
  "Typography",
  "Layout Systems",
  "Iconography",
  "Brand Application",
  "Visual Direction",
];

const meta = [
  ["Client", "Orica"],
  ["Discipline", "Presentation Design / Information Design"],
  ["Deliverables", "Corporate Presentations & Decks"],
  ["Role", "Designer"],
];

function Slide({ src, caption, onOpen, className = "", imgClass = "", contain = false, testId }) {
  return (
    <figure className={`group border border-white/10 bg-[#121418] ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Expand ${caption}`}
        className={`relative block w-full overflow-hidden text-left ${contain ? "bg-[#2d2b6c] p-4 sm:p-6" : ""}`}
      >
        <img src={src} alt={caption} loading="lazy" className={`w-full transition-transform duration-[1.2s] ease-out group-hover:scale-[1.015] ${contain ? "object-contain" : "object-cover"} ${imgClass}`} />
        <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center border border-white/20 bg-[#0B0C0E]/70 text-white/80 opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
          <Maximize2 size={13} />
        </span>
      </button>
      <figcaption className="flex items-center justify-between border-t border-white/10 px-4 py-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">{caption}</span>
        <span className="hidden font-mono text-[9px] uppercase tracking-[0.2em] text-[#616775] sm:inline">Tap to expand</span>
      </figcaption>
    </figure>
  );
}

function SectionHead({ num, label, title, children }) {
  return (
    <div className="grid gap-8 md:grid-cols-12">
      <FadeUp className="md:col-span-4">
        <p className="eyebrow">{num} · {label}</p>
      </FadeUp>
      <FadeUp className="md:col-span-7" delay={0.1}>
        <h2 className="text-2xl font-light tracking-tight sm:text-3xl">{title}</h2>
        {children}
      </FadeUp>
    </div>
  );
}

const P = ({ children }) => (
  <p className="mt-6 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">{children}</p>
);

export default function OricaCase() {
  const [lb, setLb] = useState({ items: slides, index: null });

  useEffect(() => {
    document.title = "Orica: Presentation & Deck Design · Haus Ov Harii";
  }, []);

  const open = (items, index) => setLb({ items, index });
  const close = useCallback(() => setLb((s) => ({ ...s, index: null })), []);
  const step = useCallback((d) => setLb((s) => ({ ...s, index: (s.index + d + s.items.length) % s.items.length })), []);

  return (
    <article className="px-6 pt-32 lg:px-12" data-testid="orica-case-study">
      <FadeUp>
        <Link to="/corporate" data-testid="back-to-route-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9EA3B0] transition-colors hover:text-white">
          <ArrowLeft size={14} /> Corporate
        </Link>
      </FadeUp>

      <header className="mt-12 max-w-4xl" data-testid="orica-hero">
        <p className="eyebrow mb-6">04 · Orica</p>
        <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
          <MaskedLines lines={["Orica", "Presentation & Deck Design"]} delay={0.1} />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 text-base font-light leading-relaxed text-[#9EA3B0] sm:text-lg">
            Transforming complex corporate information into clear, structured and engaging visual
            communications. Presentation design as information design.
          </p>
        </FadeUp>
      </header>

      <FadeUp className="mt-14" delay={0.1}>
        <dl className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4" data-testid="case-meta">
          {meta.map(([k, v]) => (
            <div key={k} className="bg-[#0B0C0E] p-5">
              <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">{k}</dt>
              <dd className="mt-2 text-sm text-[#F9F9FB]">{v}</dd>
            </div>
          ))}
        </dl>
      </FadeUp>

      <FadeUp className="mt-14">
        <div className="bg-[#121418] px-4 py-10 sm:px-10 sm:py-16 lg:px-24 lg:py-20">
          <Slide
            src={slides[1].src}
            caption="Talent Status Update, Talent Snapshot slide"
            onOpen={() => open(slides, 1)}
            className="mx-auto max-w-5xl shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]"
            testId="orica-hero-slide"
          />
        </div>
      </FadeUp>

      <section className="mt-24" aria-label="Project overview" data-testid="orica-overview">
        <SectionHead num="01" label="Project Overview" title="Presentation design that has to do more than style a slide.">
          <P>
            I designed corporate presentations and decks for Orica, taking complex business content and
            translating it into clear, coherent and visually engaging communication. The Talent Status
            Update shown here is representative of that work: an internal deck carrying a large volume of
            people data (populations, performance, potential, succession, risk) for an audience that
            needs to understand it in a meeting, not study it afterwards.
          </P>
          <P>
            The brief was never simply to make slides look better. The design had to establish hierarchy
            where the content arrived flat, make dense information easier to read at presentation scale,
            organise dozens of data points into something the eye can move through, and hold a consistent
            visual language across content types that behave very differently: a statistical overview, a
            nine-cell matrix, a list of recommended actions.
          </P>
        </SectionHead>
      </section>

      <section className="mt-24 border-t border-white/10 pt-16" aria-label="Turning complexity into clarity" data-testid="orica-clarity">
        <SectionHead num="02" label="Turning Complexity Into Clarity" title="Order is designed, not found.">
          <P>
            The Talent Status Update is genuinely information-dense. A single presentation covers talent
            snapshots, employee populations, performance and growth potential, succession planning,
            development actions, risk of loss and recommended actions. Left as text and tables, that
            material reads as a report. My job was to make it read as a presentation: scannable,
            comparable, and understandable from the back of the room.
          </P>
          <P>
            Every slide creates order through the same set of devices, applied deliberately:
          </P>
        </SectionHead>
        <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="clarity-devices">
          {clarityDevices.map((d, i) => (
            <FadeUp key={d} delay={(i % 3) * 0.06}>
              <div className="group flex h-full items-start justify-between gap-6 bg-[#0B0C0E] p-6 transition-colors duration-500 hover:bg-[#121418]">
                <p className="text-sm leading-relaxed text-[#F9F9FB]">{d}</p>
                <span className="font-mono text-xs text-[#616775] transition-colors duration-500 group-hover:text-umber">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </FadeUp>
          ))}
        </div>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <FadeUp>
            <Slide src={slides[2].src} caption="Progression: potential × performance matrix" onOpen={() => open(slides, 2)} testId="clarity-slide-progression" />
          </FadeUp>
          <FadeUp delay={0.1}>
            <Slide src={slides[3].src} caption="Actions: impact × risk of loss" onOpen={() => open(slides, 3)} testId="clarity-slide-actions" />
          </FadeUp>
        </div>
      </section>

      <section className="mt-24 border-t border-white/10 pt-16" aria-label="Talent Snapshot" data-testid="orica-snapshot">
        <SectionHead num="03" label="Talent Snapshot" title="Seven kinds of information, one slide, no chaos.">
          <P>
            The Talent Snapshot combines numerical statistics, employee pictograms, percentages, progress
            indicators, a pie-style performance graphic, high-priority talent information, written
            recommendations and supporting commentary within a single visual system.
          </P>
          <P>
            The challenge was to give each of those levels of information its own hierarchy (the headline
            figures first, the proportions second, the commentary last) without allowing the slide to
            become visually chaotic. Zoning, consistent scale and a disciplined type system do the work.
          </P>
        </SectionHead>
        <FadeUp className="mt-12">
          <div className="bg-[#121418] px-4 py-10 sm:px-10 sm:py-14 lg:px-20">
            <Slide src={slides[1].src} caption="Talent Snapshot, full slide" onOpen={() => open(slides, 1)} className="mx-auto max-w-5xl" testId="snapshot-full-slide" />
          </div>
        </FadeUp>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="snapshot-details">
          {snapshotDetails.map((d, i) => (
            <FadeUp key={d.src} delay={(i % 3) * 0.08}>
              <Slide src={d.src} caption={d.caption} onOpen={() => open(snapshotDetails, i)} contain imgClass="aspect-[3/2]" testId={`snapshot-detail-${i + 1}`} />
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="mt-24 border-t border-white/10 pt-16" aria-label="Designing systems, not individual slides" data-testid="orica-systems">
        <SectionHead num="04" label="Designing Systems, Not Individual Slides" title="A presentation is a system that happens to be shown one page at a time.">
          <P>
            A strong deck has to work across content types that have almost nothing in common. The
            Progression slide plots potential against performance; the Actions slide plots impact of loss
            against risk of loss. The information changes completely. The design language does not.
          </P>
          <P>
            Same grid, same module, same iconography, same colour logic. Once an audience has read one
            matrix, they can read the next without being taught again. That is what brand coherence
            means inside a complex multi-slide presentation: fewer things to learn, more attention left
            for the content.
          </P>
        </SectionHead>
        <div className="mt-12 grid gap-6 lg:grid-cols-12">
          <div className="grid gap-6 lg:col-span-7">
            <FadeUp>
              <Slide src={slides[2].src} caption="Progression" onOpen={() => open(slides, 2)} testId="systems-slide-progression" />
            </FadeUp>
            <FadeUp delay={0.1}>
              <Slide src={slides[3].src} caption="Actions" onOpen={() => open(slides, 3)} testId="systems-slide-actions" />
            </FadeUp>
          </div>
          <div className="border-t border-white/10 lg:col-span-5 lg:border-t-0 lg:border-l lg:pl-8">
            {systemTraits.map(([t, d], i) => (
              <FadeUp key={t} delay={i * 0.04} className="grid gap-1 border-b border-white/10 py-5">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-xs text-umber">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-base font-medium tracking-tight text-[#F9F9FB]">{t}</h3>
                </div>
                <p className="pl-8 text-sm leading-relaxed text-[#9EA3B0]">{d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-24 border-t border-white/10 pt-16" aria-label="Data and visual communication" data-testid="orica-data">
        <SectionHead num="05" label="Data & Visual Communication" title="Scan, compare, understand.">
          <P>
            Charts, icons, matrices and indicators are not decoration here. Each device lets an audience
            do one thing faster, whether counting a population, weighing a proportion or spotting the red cell, so that the
            conversation can move to what the data means.
          </P>
        </SectionHead>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="data-modules">
          {dataModules.map((m, i) => (
            <motion.div
              key={m.src + i}
              className={m.wide ? "sm:col-span-2" : ""}
              initial={{ opacity: 0, y: 32, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.75, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
            >
              <Slide
                src={m.src}
                caption={m.caption}
                onOpen={() => open(dataModules, i)}
                contain
                imgClass={m.wide ? "aspect-[8/3]" : "aspect-[4/3]"}
                testId={`data-module-${i + 1}`}
              />
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mt-24 border-t border-white/10 pt-16" aria-label="Presentation gallery" data-testid="orica-gallery">
        <FadeUp>
          <p className="eyebrow mb-4">06 · Presentation Gallery</p>
          <p className="mb-10 max-w-xl text-sm leading-relaxed text-[#9EA3B0]">
            The supplied Talent Status Update slides, as presented. Click any slide to view it at full size.
          </p>
        </FadeUp>
        <div className="grid gap-8 md:grid-cols-2">
          {slides.map((s, i) => (
            <FadeUp key={s.src} delay={(i % 2) * 0.1}>
              <div className="bg-[#121418] p-4 sm:p-8">
                <Slide src={s.src} caption={`${String(i + 1).padStart(2, "0")} · ${s.caption}`} onOpen={() => open(slides, i)} testId={`gallery-slide-${i + 1}`} />
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="mt-24 border-y border-white/10 py-20" aria-label="Closing statement" data-testid="orica-closing">
        <FadeUp>
          <p className="max-w-4xl text-3xl font-light leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
            Complex information needs structure before it needs decoration.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
            The Orica work brings together brand consistency, typography, data visualisation and information
            hierarchy to create corporate presentations that stay clear even when the subject matter is not.
            It is evidence of a particular skill: communicating complicated corporate material visually,
            rather than simply making attractive slides.
          </p>
        </FadeUp>
      </section>

      <section className="mt-20" aria-label="Skills demonstrated" data-testid="orica-skills">
        <FadeUp>
          <p className="eyebrow mb-8">07 · Skills</p>
        </FadeUp>
        <div className="flex flex-wrap gap-2">
          {skills.map((s, i) => (
            <FadeUp key={s} delay={i * 0.03}>
              <span
                className="inline-block border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EA3B0] transition-colors duration-300 hover:border-umber hover:text-umber"
                data-testid={`skill-chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {s}
              </span>
            </FadeUp>
          ))}
        </div>
      </section>

      <nav className="mt-24 grid border-t border-white/10 md:grid-cols-2" aria-label="Project navigation">
        <Link to="/corporate/jp-morgan-chase" data-testid="prev-project-link" className="group block border-b border-white/10 py-16 md:border-b-0 md:border-r md:pr-8">
          <p className="eyebrow mb-4">Previous corporate project</p>
          <span className="flex items-center gap-4">
            <ArrowLeft className="shrink-0 text-[#616775] transition-all duration-500 group-hover:-translate-x-2 group-hover:text-umber" size={28} />
            <span className="text-2xl font-light tracking-tight transition-transform duration-500 group-hover:-translate-x-2 sm:text-4xl">
              Brand Communications
            </span>
          </span>
        </Link>
        <Link to="/corporate/valiant-un-bid" data-testid="next-project-link" className="group block py-16 md:pl-8 md:text-right">
          <p className="eyebrow mb-4">Next corporate project</p>
          <span className="flex items-center gap-4 md:justify-end">
            <span className="text-2xl font-light tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-4xl">
              UN Financial Bid Proposal
            </span>
            <ArrowRight className="shrink-0 text-[#616775] transition-all duration-500 group-hover:translate-x-2 group-hover:text-umber" size={28} />
          </span>
        </Link>
      </nav>

      <Lightbox items={lb.items} index={lb.index} onClose={close} onStep={step} />
    </article>
  );
}
